import {
  DoneAll,
  Info as InfoIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Search as SearchIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Badge,
  Box,
  ClickAwayListener,
  Grow,
  IconButton,
  ListItemText,
  MenuList,
  Paper,
  Popper,
  Skeleton,
  Tooltip,
  Typography,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useAccount, useChat, useToast } from "../../../context";
import { useMessageReadStatus, useOnlineStatus } from "../../../hooks";
import { CallDialog } from "../CallDialog/CallDialog";
import Header from "../Header/Header";
import { useChatStore } from "../../../store/chatStore";
import { deleteConversationHistory } from "../../../services/api";

const HeaderInfo = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 12,
  flex: 1,
  minWidth: 0,
});

const AvatarContainer = styled(Box)({
  position: "relative",
  flexShrink: 0,
});

const ContactInfo = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  flex: 1,
});

const Name = styled(Box)({
  fontSize: "16px",
  fontWeight: 500,
  color: "#111b21",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const Status = styled(Box)({
  fontSize: "13px",
  color: "#667781",
});

const TypingStatus = styled(Box)({
  fontSize: "13px",
  color: "#00a884",
  fontWeight: 500,
  display: "flex",
  alignItems: "center",
  gap: "1px",
  "@keyframes typingDots": {
    "0%, 100%": { opacity: 0.2 },
    "50%": { opacity: 1 },
  },
  "& span": {
    animation: "typingDots 1.4s infinite",
    display: "inline-block",
    "&:nth-of-type(2)": {
      animationDelay: "0.2s",
    },
    "&:nth-of-type(3)": {
      animationDelay: "0.4s",
    },
  },
});

const RightContainer = styled(Box)({
  display: "flex",
  gap: 8,
  marginLeft: "auto",
  flexShrink: 0,
});

const StyledIconButton = styled(IconButton)({
  color: "#54656f",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
});

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}));

const StyledMenuList = styled(MenuList)({
  padding: "8px 0",
  minWidth: "200px",
});

const StyledMenuItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  padding: "8px 16px",
  cursor: "pointer",
  color: "#111b21",
  "&:hover": {
    backgroundColor: "#f5f6f6",
  },
  "& svg": {
    marginRight: "12px",
    color: "#54656f",
  },
});

interface ChatHeaderProps {
  onSearchToggle: (isOpen: boolean, type?: "search" | "contact") => void;
  isSearchOpen: boolean;
  onAllRead?: () => void;
  infoPanelWidth?: number;
  isInfoPanelResizing?: boolean;
}

const ChatHeader: React.FC<ChatHeaderProps> = React.memo(
  ({
    onSearchToggle,
    isSearchOpen,
    onAllRead,
    infoPanelWidth = 350,
    isInfoPanelResizing = false,
  }) => {
    const [loading] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [openCallDialog, setOpenCallDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const { selectedConversation } = useChat();
    const { socket } = useAccount();
    const { showError, showSuccess } = useToast();
    const menuRef = useRef<HTMLDivElement>(null);

    const typingState = useChatStore((state) => state.typingState);
    const activePhone = selectedConversation
      ? selectedConversation.valid_telephone || selectedConversation.telephone
      : "";
    const isTyping = activePhone ? !!typingState[activePhone] : false;

    // Memoizar as props do hook para evitar re-renderizações desnecessárias
    const messageReadStatusProps = useMemo(
      () => ({
        socket,
        selectedConversation: selectedConversation || null,
      }),
      [socket, selectedConversation],
    );

    const { markAllAsRead, isLoading: isMarkingAsRead } = useMessageReadStatus(
      messageReadStatusProps,
    );

    // Memoizar as props do hook de status online
    const onlineStatusProps = useMemo(
      () => ({
        conversation: selectedConversation,
      }),
      [selectedConversation],
    );

    // Usar o hook de status online
    const { isOnline, lastSeen } = useOnlineStatus(onlineStatusProps);

    const handleMenuClick = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
      },
      [],
    );

    const handleClose = useCallback(() => {
      setAnchorEl(null);
    }, []);

    const handleSearchClick = useCallback(() => {
      onSearchToggle(true, "search");
      handleClose();
    }, [onSearchToggle, handleClose]);

    const handleInfoClick = useCallback(() => {
      onSearchToggle(true, "contact");
      handleClose();
    }, [onSearchToggle, handleClose]);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (event.key === "Escape") {
          handleClose();
        }
      },
      [handleClose],
    );

    const handleMarkAllAsRead = useCallback(async () => {
      await markAllAsRead();
      if (onAllRead) onAllRead();
    }, [markAllAsRead, onAllRead]);

    const handleDeleteHistory = useCallback(async () => {
      if (!selectedConversation) return;
      setIsDeleting(true);
      try {
        await deleteConversationHistory(
          selectedConversation.id || selectedConversation.telephone,
        );
        showSuccess("Histórico apagado com sucesso");
        setOpenDeleteDialog(false);
        handleClose();
        // Recarregar a página ou resetar a conversa para atualizar o frontend
        setTimeout(() => window.location.reload(), 1000);
      } catch (error) {
        showError("Erro ao apagar histórico.");
      } finally {
        setIsDeleting(false);
      }
    }, [selectedConversation, showError, handleClose]);

    const handleOpenCallDialog = useCallback(() => {
      if (!selectedConversation) return;
      const toRaw =
        selectedConversation.valid_telephone ||
        selectedConversation.telephone ||
        "";
      const to = String(toRaw).replace(/\D/g, "");
      const companyId = selectedConversation.company_id;

      if (!to || !companyId) {
        showError(
          "Não foi possível identificar o telefone ou a empresa desta conversa.",
        );
        return;
      }

      if (!socket.current?.connected) {
        showError(
          "Socket não conectado. Aguarde reconectar e tente novamente.",
        );
        return;
      }

      setOpenCallDialog(true);
    }, [selectedConversation, showError, socket]);

    // Memoizar o status de loading
    const loadingContent = useMemo(
      () => (
        <Box
          sx={{
            height: "60px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 16px",
            borderBottom: "1px solid #e9edef",
            backgroundColor: "#f0f2f5",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box>
              <Skeleton variant="text" width={120} height={20} />
              <Skeleton variant="text" width={80} height={16} />
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="circular" width={32} height={32} />
          </Box>
        </Box>
      ),
      [],
    );

    // Memoizar o conteúdo quando não há conversa selecionada
    const noConversationContent = useMemo(
      () => (
        <Box
          sx={{
            marginRight: isSearchOpen ? `${infoPanelWidth}px` : 0,
            transition: isInfoPanelResizing
              ? "none"
              : "margin-right 0.15s ease-out",
          }}
        >
          <Header>
            <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
              <Typography variant="h6" sx={{ color: "#667781", flex: 1 }}>
                Selecione uma conversa
              </Typography>
            </Box>
          </Header>
        </Box>
      ),
      [isSearchOpen, infoPanelWidth, isInfoPanelResizing],
    );

    if (loading) {
      return loadingContent;
    }

    if (!selectedConversation) {
      return noConversationContent;
    }

    return (
      <>
        <Box
          sx={{
            marginRight: isSearchOpen ? `${infoPanelWidth}px` : 0,
            transition: isInfoPanelResizing
              ? "none"
              : "margin-right 0.15s ease-out",
          }}
        >
          <Header>
            <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
              <HeaderInfo
                onClick={() => onSearchToggle(true, "contact")}
                sx={{ cursor: "pointer" }}
              >
                <AvatarContainer>
                  <StyledBadge
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    variant="dot"
                    invisible={!isOnline}
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: "#bdbdbd",
                        transition:
                          "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
                        willChange: "transform",
                        "&:hover": {
                          transform: "scale(1.15) rotate(6deg)",
                        },
                      }}
                    >
                      <PersonIcon sx={{ width: 30, height: 30 }} />
                    </Avatar>
                  </StyledBadge>
                </AvatarContainer>
                <ContactInfo>
                  <Name>
                    {(
                      selectedConversation.valid_name ||
                      selectedConversation.name ||
                      "Desconhecido"
                    )
                      .split(" ")
                      .map(
                        (word) =>
                          word.charAt(0).toUpperCase() +
                          word.slice(1).toLowerCase(),
                      )
                      .join(" ")}
                  </Name>
                  {isTyping ? (
                    <TypingStatus>
                      digitando<span>.</span>
                      <span>.</span>
                      <span>.</span>
                    </TypingStatus>
                  ) : (
                    <Status>
                      {isOnline
                        ? "online"
                        : lastSeen
                          ? `visto por último ${formatDistanceToNow(lastSeen, { addSuffix: true, locale: ptBR })}`
                          : "nunca visto"}
                    </Status>
                  )}
                </ContactInfo>
              </HeaderInfo>
              <RightContainer>
                {/* Se o botão estiver desativado, exibir o tooltip "Funcionalidade em desenvolvimento" */}
                <Tooltip title="Funcionalidade em desenvolvimento">
                  <span>
                    <StyledIconButton
                      onClick={handleOpenCallDialog}
                      disabled={true}
                    >
                      <PhoneIcon />
                    </StyledIconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Buscar mensagens">
                  <StyledIconButton onClick={handleSearchClick}>
                    <SearchIcon />
                  </StyledIconButton>
                </Tooltip>
                <Tooltip title="Menu">
                  <StyledIconButton onClick={handleMenuClick}>
                    <MoreVertIcon />
                  </StyledIconButton>
                </Tooltip>
              </RightContainer>
            </Box>
          </Header>
        </Box>

        <Popper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="bottom-end"
          transition
          disablePortal
          ref={menuRef}
          style={{ zIndex: 1300 }}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <Paper
                elevation={0}
                sx={{
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 1.5,
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                }}
              >
                <ClickAwayListener onClickAway={handleClose}>
                  <StyledMenuList
                    onKeyDown={handleKeyDown}
                    role="menu"
                    sx={{ padding: "0px" }}
                    aria-label="Menu de opções do chat"
                  >
                    <StyledMenuItem onClick={handleInfoClick} role="menuitem">
                      <InfoIcon fontSize="small" />
                      <ListItemText>Informações do contato</ListItemText>
                    </StyledMenuItem>
                    <StyledMenuItem onClick={handleSearchClick} role="menuitem">
                      <SearchIcon fontSize="small" />
                      <ListItemText>Buscar mensagens</ListItemText>
                    </StyledMenuItem>
                    <StyledMenuItem
                      onClick={handleMarkAllAsRead}
                      role="menuitem"
                    >
                      <DoneAll fontSize="small" />
                      <ListItemText>
                        {isMarkingAsRead
                          ? "Marcando..."
                          : "Marcar todas como lidas"}
                      </ListItemText>
                    </StyledMenuItem>
                    <StyledMenuItem
                      onClick={() => setOpenDeleteDialog(true)}
                      role="menuitem"
                      sx={{
                        color: "#d32f2f",
                        "& svg": { color: "#d32f2f" },
                        "&:hover": { backgroundColor: "#ffebee" },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                      <ListItemText>Limpar Histórico</ListItemText>
                    </StyledMenuItem>
                  </StyledMenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>

        {selectedConversation && (
          <CallDialog
            open={openCallDialog}
            onClose={() => setOpenCallDialog(false)}
            companyId={selectedConversation.company_id}
            to={String(
              selectedConversation.valid_telephone ||
                selectedConversation.telephone ||
                "",
            ).replace(/\D/g, "")}
          />
        )}

        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
        >
          <DialogTitle>Limpar Histórico de Mensagens</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja apagar todo o histórico de mensagens com
              este cliente? Esta ação não pode ser desfeita, e apagará o
              contexto que a Inteligência Artificial possui dessa conversa.
              (Nota: As mensagens não são apagadas do aplicativo WhatsApp do
              cliente).
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenDeleteDialog(false)}
              color="primary"
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteHistory}
              color="error"
              autoFocus
              disabled={isDeleting}
            >
              {isDeleting ? "Apagando..." : "Apagar"}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  },
);

ChatHeader.displayName = "ChatHeader";

export default ChatHeader;
